import React from "react";
import { ArrowLeft, CreditCard, ShieldCheck, BadgeCheck, Signal, HandHeart, ArrowRight, Gift } from "lucide-react";
import { SPONSOR } from "@/constants/testIds";
import PricingCard from "./PricingCard";
import Timeline from "./Timeline";

const features = [
  {
    icon: CreditCard,
    title: "Prices in GBP",
    desc: "Transparent fees. Secure card payment via Stripe — sent after the free suitability call.",
  },
  {
    icon: ShieldCheck,
    title: "Dual-jurisdiction compliance",
    desc: "Operating under UK data-processing standards and Zimbabwe's Cyber and Data Protection Act (2021).",
  },
  {
    icon: BadgeCheck,
    title: "Vetted professional counsellors",
    desc: "Trained, supervised, and police-cleared — the highest standards of confidentiality at all times.",
  },
  {
    icon: Signal,
    title: "Traditional calls",
    desc: "We call your relative at mutually scheduled times — no Wi-Fi or data needed on their end.",
  },
];

const steps = [
  {
    title: "Express your interest",
    description:
      "Fill in our short form with your contact details and your relative's details. There's an optional tick-box — \"keep me informed of their progress\" — so we can confirm attendance to you. We never share what's discussed in sessions, only that a session took place.",
  },
  {
    title: "Relative's suitability call (free)",
    description:
      "A short, no-cost phone appointment with your relative to understand why counselling is being sought and check that telephone counselling is the right fit — or refer them on if it isn't.",
    trust: "Free clinical check before billing",
  },
  {
    title: "Payment & booking",
    description:
      "Once we've established we're a good fit, we send you a secure Stripe payment link by email or WhatsApp. When payment clears, we contact your relative to book their session.",
  },
  {
    title: "Scheduled session",
    description:
      "Your relative receives the call on their local line, on the day and time agreed with them.",
  },
];

export const SponsorPage = ({ onBack, onBook, isHome = false }) => {
  return (
    <main data-testid={SPONSOR.view} className="hr-paper-bg">
      <section className="max-w-2xl mx-auto px-6 sm:px-8 pt-6 pb-32 sm:pb-40">
        {!isHome && (
          <button
            type="button"
            onClick={onBack}
            data-testid={SPONSOR.back}
            className="inline-flex items-center gap-2 text-sm text-[#1F2E4D] hover:text-[#0E7C7B] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden />
            Back
          </button>
        )}

        <header className={`${isHome ? "mt-2 sm:mt-6" : "mt-8"} hr-fade-up`}>
          <p className="text-xs tracking-[0.22em] uppercase font-semibold text-[#1F2E4D]">
            {isHome ? "Welcome" : "For family abroad"}
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight text-[#0E7C7B] mt-3">
            Sponsor peace of mind for your relative in Zimbabwe.
          </h1>
          <p className="text-base sm:text-lg leading-relaxed text-[#4A5568] mt-5">
            Helping the best way you can means making it effortless for them.
            Clear calls over regular phone lines require no data bundles or
            internet. We keep you connected with email attendance updates,
            while keeping their sessions completely confidential.
          </p>
        </header>

        {/* Features */}
        <section className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-6 hr-fade-up hr-fade-up-delay-2">
          {features.map(({ icon: Icon, title }) => (
            <div key={title} className="flex gap-4">
              <span className="mt-1 inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#E0EBEB] shrink-0">
                <Icon className="w-5 h-5 text-[#0E7C7B]" aria-hidden />
              </span>
              <div>
                <h3 className="font-serif text-lg text-[#1F2E4D] leading-snug">
                  {title}
                </h3>
              </div>
            </div>
          ))}
        </section>

        <hr className="hr-divider my-14" />

        {/* Pricing */}
        <section>
          <p className="text-xs tracking-[0.22em] uppercase font-semibold text-[#0E7C7B]">
            Sponsorship pricing
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl text-[#1F2E4D] mt-3 leading-tight">
            A clearly priced meaningful gift.
          </h2>

          <div className="mt-8 flex flex-col gap-6">
            <PricingCard
              testIdCard={SPONSOR.cardSingle}
              eyebrow="Single sponsorship"
              title="Sponsor a Session"
              price="£35"
              subPrice="session"
              duration="One 60-minute call for your relative"
              bullets={[
                "Clinical suitability check first — free of charge",
                "Secure Stripe payment link sent after the check",
                "Discreet — your relative chooses what to share",
              ]}
            />
            <PricingCard
              testIdCard={SPONSOR.cardBundle}
              eyebrow="Multi-session sponsorship"
              title="4-Session Sponsorship Bundle"
              price="£120"
              subPrice="bundle"
              duration="Four 60-minute calls for your relative"
              bullets={[
                "Bundle discount — save £20",
                "Clinical suitability check first — free of charge",
                "Peace of mind — more sessions for ongoing support",
              ]}
              accent
            />

            {/* 10-Session Extended Care Bundle */}
            <article
              data-testid={SPONSOR.cardDonate}
              className="relative bg-[#E0EBEB] rounded-2xl p-7 sm:p-8 border border-[#0E7C7B]/25 hr-card-shadow flex flex-col overflow-hidden"
            >
              <span
                className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-white/40 blur-2xl"
                aria-hidden
              />
              <div className="relative">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#0E7C7B]">
                    Extended care bundle
                  </p>
                  <span className="text-[10px] tracking-[0.18em] uppercase font-semibold px-2.5 py-1 rounded-full bg-[#0E7C7B] text-white">
                    Save £70
                  </span>
                </div>
                <h3 className="font-serif text-2xl sm:text-[28px] leading-snug text-[#1F2E4D] mt-4">
                  10-Session Sponsorship Bundle
                </h3>
                <p className="text-[15px] text-[#1F2E4D] leading-relaxed mt-3">
                  Ten weekly 60-minute calls for your relative — the deepest
                  level of consistent support, at the lowest per-session
                  price.
                </p>

                <div className="flex items-baseline gap-3 mt-5">
                  <span className="font-serif text-3xl sm:text-4xl text-[#1F2E4D]">
                    £280
                  </span>
                  <span className="text-sm text-[#4A5568] line-through">£350</span>
                  <span className="text-sm text-[#0E7C7B] font-semibold">
                    Save £70
                  </span>
                </div>
                <p className="text-xs text-[#4A5568] mt-1">
                  £28 per session — our best per-session value.
                </p>

                <ul className="mt-5 space-y-2.5">
                  <li className="flex items-start gap-3 text-[15px] text-[#1F2E4D]">
                    <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-white">
                      <ShieldCheck className="w-3 h-3 text-[#0E7C7B]" aria-hidden />
                    </span>
                    <span className="leading-relaxed">Clinical suitability check first — free of charge</span>
                  </li>
                  <li className="flex items-start gap-3 text-[15px] text-[#1F2E4D]">
                    <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-white">
                      <HandHeart className="w-3 h-3 text-[#0E7C7B]" aria-hidden />
                    </span>
                    <span className="leading-relaxed">Best per-session price (£28 / session)</span>
                  </li>
                  <li className="flex items-start gap-3 text-[15px] text-[#1F2E4D]">
                    <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-white">
                      <Gift className="w-3 h-3 text-[#0E7C7B]" aria-hidden />
                    </span>
                    <span className="leading-relaxed">Sustained, weekly support for your relative</span>
                  </li>
                </ul>
              </div>
            </article>
          </div>
        </section>

        <hr className="hr-divider my-14" />

        {/* How to Book */}
        <section>
          <p className="text-xs tracking-[0.22em] uppercase font-semibold text-[#0E7C7B]">
            How sponsorship works
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl text-[#1F2E4D] mt-3 leading-tight">
            Steps to feeling like yourself again.
          </h2>
          <div className="mt-10">
            <Timeline steps={steps} />
          </div>
        </section>

        <p className="mt-14 text-sm text-[#4A5568] leading-relaxed">
          Your data is managed securely in line with{" "}
          <span className="text-[#1F2E4D] font-semibold">
            Zimbabwe's Cyber and Data Protection Act (2021)
          </span>{" "}
          and{" "}
          <span className="text-[#1F2E4D] font-semibold">UK GDPR</span>. We
          will never share your private information without your consent.
        </p>
      </section>

      {/* Sticky CTA */}
      <div className="fixed inset-x-0 bottom-0 z-20 bg-[#F6F1E8]/95 backdrop-blur border-t border-[#E8E2D4]">
        <div className="max-w-2xl mx-auto px-6 sm:px-8 py-3.5">
          <button
            type="button"
            onClick={() => onBook("Sponsor a Session (UK/Global) — £35")}
            data-testid={SPONSOR.stickyCta}
            className="w-full bg-[#0E7C7B] hover:bg-[#0b6362] text-white rounded-full px-6 py-4 font-medium transition-colors hr-pill-shadow"
          >
            Sponsor a session
          </button>
        </div>
      </div>
    </main>
  );
};

export default SponsorPage;
